import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Square, Minus, Plus, Share2, Edit, Trash2 } from 'lucide-react';
import AddToHubButton from '@/components/hub/add-to-hub-button';
import { useTimerStore } from '@/store/useTimerStore';
import { useCareRecipientView } from '@/contexts/care-recipient-view-context';
import { useLocation } from 'wouter';
import { fetchWithAuth } from '@/lib/fetchWithAuth';

interface RectangularTimerProps {
  timer: {
    id: number;
    title: string;
    segments: Array<{
      id: string;
      type: 'text' | 'image' | 'video';
      content: string;
      duration: number;
    }>;
    currentTimerState?: any;
    timeRemaining?: number;
    remainingTime?: number;
    remainingSeconds?: number;
    isRunning?: boolean;
    isPaused?: boolean;
    state?: string;
    showInCareView?: boolean;
    startedAt?: number | string;
    startTime?: number | string;
    status?: string;
    endsAtMs?: number;
    endsAt?: string;
    durationSeconds?: number;
  };
  onDelete?: (id: number) => void;
  onEdit?: (timer: any) => void;
  onToggleCareView?: (timer: any, show: boolean) => void;
  onShare?: (timer: any) => void;
  viewOnly?: boolean;
  showControls?: boolean;
  hideButtons?: boolean;
  readonly?: boolean;
}

export default function RectangularTimer({ 
  timer, 
  onDelete, 
  onEdit, 
  onToggleCareView,
  onShare,
  viewOnly = false, 
  showControls = true, 
  hideButtons = false, 
  readonly = false 
}: RectangularTimerProps) {
  const { isInCareRecipientView } = useCareRecipientView();
  const [location] = useLocation();

  // Check if we're in care recipient view based on URL
  const isInCareView = isInCareRecipientView || location.includes('/care-recipient-view');

  // STABLE SELECTORS: Use individual selectors to avoid creating new objects on every render
  // This prevents infinite re-render loops
  const timerState = useTimerStore((state) => state.timers[timer.id]);
  const startTimer = useTimerStore((state) => state.startTimer);
  const pauseTimer = useTimerStore((state) => state.pauseTimer);
  const resetTimer = useTimerStore((state) => state.resetTimer);
  const restartTimer = useTimerStore((state) => state.restartTimer);
  const createTimerAction = useTimerStore((state) => state.createTimer);
  
  // Wrap functions for care view (no-ops)
  const safeStartTimer = isInCareView ? async () => {} : startTimer;
  const safePauseTimer = isInCareView ? async () => {} : pauseTimer;
  const safeResetTimer = isInCareView ? async () => {} : resetTimer;
  const safeRestartTimer = isInCareView ? async () => {} : restartTimer;
  
  // Track if we've initialized this timer in the store
  const hasInitializedRef = useRef(false);
  
  // Calculate total duration (memoized to avoid recalculation)
  const totalDuration = useMemo(() => 
    timer.segments?.reduce((sum, section) => sum + section.duration, 0) ?? 0
  , [timer.segments]);
  
  // Ensure timer exists in store before starting - SKIP in care view
  // CRITICAL: Use ref to run only once, use getState() to avoid store subscription
  useEffect(() => {
    if (isInCareView) return;
    if (hasInitializedRef.current) return;
    if (!timer.segments || timer.segments.length === 0) return;
    
    // Check store state directly (no subscription)
    const currentStoreState = useTimerStore.getState();
    if (currentStoreState.timers[timer.id]) {
      hasInitializedRef.current = true;
      return;
    }
    
    hasInitializedRef.current = true;
    console.log(`[RectangularTimer] Creating timer ${timer.id} with duration ${totalDuration}s`);
    currentStoreState.createTimer({
      id: timer.id,
      duration: totalDuration,
      title: timer.title,
      segments: timer.segments,
      timeRemaining: totalDuration,
      isRunning: false,
      isPaused: false
    });
  }, [timer.id, isInCareView]); // Minimal dependencies - only run when timer.id or view mode changes

  // Determine timer state based on context - read directly, no effects needed
  // CRITICAL: Prefer store state, but fallback to API props if store is empty (e.g., after page refresh)
  const timeRemaining = isInCareView 
    ? (timer.remainingTime ?? totalDuration)
    : (timerState?.timeRemaining ?? timer.timeRemaining ?? timer.remainingTime ?? totalDuration);
  // CRITICAL: Use OR logic for status check, not nullish coalescing
  // because timer.status === 'running' returns boolean, not undefined
  const isRunning = isInCareView 
    ? (timer.isRunning === true || timer.status === 'running')
    : (timerState?.isRunning ?? (timer.isRunning === true || timer.status === 'running'));
  const isPaused = isInCareView 
    ? (timer.isPaused === true || timer.status === 'paused')
    : (timerState?.isPaused ?? (timer.isPaused === true || timer.status === 'paused'));

  // SIMPLIFIED: Store now handles ALL countdown logic via hydrateRunningTimer
  // Component just reads timeRemaining from store state
  // This ensures Hub ↔ Care View synchronization and refresh recovery
  
  // Hydration on mount: If timer is running but store doesn't have it, seed from API props
  useEffect(() => {
    const storeTimer = useTimerStore.getState().timers[timer.id];
    const storeIsRunning = storeTimer?.isRunning === true || storeTimer?.status === 'running';
    const propsIsRunning = timer.isRunning === true || timer.status === 'running';
    
    // If props say running but store isn't ticking, we need to hydrate
    if (propsIsRunning && !storeIsRunning) {
      // Calculate endsAtMs from props
      let endsAtMs: number | undefined = timer.endsAtMs;
      
      // Fallback: Parse endsAt ISO string
      if (!endsAtMs && timer.endsAt) {
        const parsed = new Date(timer.endsAt).getTime();
        if (Number.isFinite(parsed)) endsAtMs = parsed;
      }
      
      // Fallback: Calculate from remaining time
      if (!endsAtMs) {
        const remaining = timer.remainingSeconds ?? timer.timeRemaining ?? timer.remainingTime;
        if (remaining != null && remaining > 0) {
          endsAtMs = Date.now() + remaining * 1000;
        }
      }
      
      if (endsAtMs && endsAtMs > Date.now()) {
        console.log('[RectangularTimerFixed] Hydrating timer from props:', { timerId: timer.id, endsAtMs });
        useTimerStore.getState().hydrateRunningTimer(timer.id, endsAtMs);
      }
    }
  }, [timer.id, timer.isRunning, timer.status, timer.endsAtMs, timer.endsAt, timer.remainingSeconds, timer.timeRemaining, timer.remainingTime]);

  // SIMPLIFIED: Just read timeRemaining from store - store handles countdown
  const displayTimeRemaining = timerState?.timeRemaining ?? timeRemaining;
  const displayIsRunning = isRunning;

  // Format time into mm:ss
  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="space-y-4">
      {/* Timer Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{timer.title}</h3>
        <div className="text-3xl font-bold text-blue-600">
          {formatTime(displayTimeRemaining)}
        </div>
        <div className="text-sm text-gray-500">
          Total: {formatTime(totalDuration)}
        </div>
      </div>

      {/* Timer Controls */}
      {!viewOnly && !readonly && !hideButtons && showControls && (
        <div className="flex justify-center items-center mt-4 gap-2">
          {displayIsRunning ? (
            <>
              <Button 
                onClick={async () => {
                  console.log('[RectangularTimer] Pause clicked for:', timer.id);
                  try {
                    await fetchWithAuth(`/api/timers/${timer.id}/pause`, { method: 'POST' });
                  } catch (error) {
                    console.error('[RectangularTimer] Server pause failed:', error);
                  }
                  await safePauseTimer(timer.id);
                }}
                variant="outline"
              >
                Pause
              </Button>
              <Button 
                onClick={async () => {
                  console.log('[RectangularTimer] Restart clicked for:', timer.id);
                  try {
                    await fetchWithAuth(`/api/timers/${timer.id}/reset`, { method: 'POST' });
                  } catch (error) {
                    console.error('[RectangularTimer] Server reset failed:', error);
                  }
                  await safeRestartTimer(timer.id);
                }}
                variant="outline"
              >
                Restart
              </Button>
            </>
          ) : (
            <>
              <Button onClick={async () => {
                console.log('[RectangularTimer] Start clicked for:', timer.id);
                
                try {
                  // CRITICAL: Call server API first to persist running state to database
                  console.log('[RectangularTimer] Calling server API to start timer:', timer.id);
                  await fetchWithAuth(`/api/timers/${timer.id}/start`, {
                    method: 'POST',
                  });
                  console.log('[RectangularTimer] Server API start successful');
                } catch (error) {
                  console.error('[RectangularTimer] Server API start failed:', error);
                  // Continue with local start anyway for UX
                }
                
                // Check store directly (no subscription) to ensure timer exists
                const storeState = useTimerStore.getState();
                if (!storeState.timers[timer.id]) {
                  console.log('[RectangularTimer] Timer not in store, creating it first');
                  await storeState.createTimer({
                    id: timer.id,
                    duration: totalDuration,
                    title: timer.title,
                    segments: timer.segments as any,
                    timeRemaining: totalDuration,
                    isRunning: false,
                    isPaused: false
                  });
                  // Wait a moment for store to update
                  await new Promise(resolve => setTimeout(resolve, 100));
                }
                
                await safeStartTimer(timer.id);
              }}>Start</Button>
              <Button 
                onClick={async () => {
                  console.log('[RectangularTimer] Restart clicked for:', timer.id);
                  try {
                    await fetchWithAuth(`/api/timers/${timer.id}/reset`, { method: 'POST' });
                  } catch (error) {
                    console.error('[RectangularTimer] Server reset failed:', error);
                  }
                  await safeRestartTimer(timer.id);
                }}
                variant="outline"
              >
                Restart
              </Button>
            </>
          )}
        </div>
      )}
      
      {/* Care View Toggle and Action Icons */}
      {!isInCareView && !viewOnly && !readonly && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show in Care View:</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={timer.showInCareView || false}
                  onChange={(e) => onToggleCareView?.(timer, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              <AddToHubButton
                resourceType="timer"
                resourceId={String(timer.id)}
                title={timer.name || timer.title}
                thumbnailUrl={timer.image}
                size="sm"
                variant="ghost"
              />
              {onShare && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onShare(timer)}
                  className="p-2 h-8 w-8"
                >
                  <Share2 className="w-4 h-4 text-blue-600" />
                </Button>
              )}
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(timer)}
                  className="p-2 h-8 w-8"
                >
                  <Edit className="w-4 h-4 text-gray-600" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(timer.id)}
                  className="p-2 h-8 w-8"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Segments Display */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {timer.segments.map((section, index) => {
          let accumulatedTime = 0;
          let segmentProgress = 0;
          let isCompleted = false;
          let isCurrent = false;

          // Calculate accumulated time up to this segment
          for (let i = 0; i < index; i++) {
            accumulatedTime += timer.segments[i].duration;
          }

          const elapsedTotal = totalDuration - displayTimeRemaining;

          if (elapsedTotal > accumulatedTime + section.duration) {
            // This segment is completed
            isCompleted = true;
            segmentProgress = 100;
          } else if (elapsedTotal > accumulatedTime) {
            // This segment is current
            isCurrent = true;
            const segmentElapsed = elapsedTotal - accumulatedTime;
            segmentProgress = (segmentElapsed / section.duration) * 100;
          }

          return (
            <div key={index} className="relative flex-shrink-0">
              {/* Section Container */}
              <div className={`aspect-square relative overflow-hidden rounded-lg border-2 ${
                isCurrent ? 'border-orange-400' : isCompleted ? 'border-green-400' : 'border-gray-300'
              }`}>
                {/* Section Image/Content */}
                {(section.content) && section.type === 'image' ? (
                  <img 
                    src={section.content} 
                    alt={`Section ${index + 1}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.log('Image failed to load:', section.content);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 text-sm text-center p-2">
                      {section.type === 'image' ? 'Image' : (section.content || section.type || 'Content')}
                    </span>
                  </div>
                )}

                {/* Progress Overlay */}
                <div
                  className="absolute inset-0 bg-gray-800 bg-opacity-70 transition-all duration-1000"
                  style={{
                    width: `${segmentProgress}%`,
                  }}
                />

                {/* Completion Check */}
                {isCompleted && (
                  <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Section Info */}
              <div className="bg-orange-400 text-white text-center py-1 px-1 rounded-b-lg">
                <div className="text-xs font-medium truncate">{section.type === 'image' ? 'Image' : section.content}</div>
                <div className="text-xs">{section.duration}s</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}